import { useState, useEffect, useCallback } from "react";
import { distributionRulesApi } from "../services/api";

/**
 * Helper to extract data from API response
 */
const extractData = (response) => {
  if (!response) return { success: false, data: null };
  const success = response.success ?? response.Success ?? false;
  const data = response.data ?? response.Data ?? null;
  const message = response.message ?? response.Message ?? null;
  return { success, data, message };
};

/**
 * Hook for fetching distribution rules
 */
export function useDistributionRules(options = {}) {
  const { activeOnly = false, autoFetch = true } = options;

  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRules = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const params = {};
      if (activeOnly) params.activeOnly = true;

      const response = await distributionRulesApi.getAll(params);
      const { success, data, message } = extractData(response);

      if (success) {
        setRules(data || []);
      } else {
        setError(message || "Failed to fetch distribution rules");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch distribution rules");
    } finally {
      setLoading(false);
    }
  }, [activeOnly]);

  useEffect(() => {
    if (autoFetch) {
      fetchRules();
    }
  }, [fetchRules, autoFetch]);

  return {
    rules,
    loading,
    error,
    refetch: fetchRules,
  };
}

/**
 * Hook for a single distribution rule
 */
export function useDistributionRule(id) {
  const [rule, setRule] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchRule = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.getById(id);
      const { success, data, message } = extractData(response);

      if (success) {
        setRule(data);
      } else {
        setError(message || "Failed to fetch distribution rule");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch distribution rule");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchRule();
  }, [fetchRule]);

  return {
    rule,
    loading,
    error,
    refetch: fetchRule,
  };
}

/**
 * Hook for distribution rule mutations
 */
export function useDistributionRuleMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const createRule = async (ruleData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.create(ruleData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateRule = async (id, ruleData) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.update(id, ruleData);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateMetadata = async (id, name, description, reason = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.updateMetadata(id, {
        name,
        description,
        reason,
      });
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const activateRule = async (id, effectiveFrom) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.activate(id, effectiveFrom);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deactivateRule = async (id, reason = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.deactivate(id, reason);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const deleteRule = async (id, reason) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.delete(id, reason);
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const previewDistribution = async (ruleId, totalAmount, location = null) => {
    setLoading(true);
    setError(null);

    try {
      const response = await distributionRulesApi.preview(
        ruleId,
        totalAmount,
        location,
      );
      return extractData(response);
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    error,
    createRule,
    updateRule,
    updateMetadata,
    activateRule,
    deactivateRule,
    deleteRule,
    previewDistribution,
  };
}

export default useDistributionRules;
